import { useLayoutEffect, type RefObject } from 'react';

/**
 * On mobile the chat panel (and the reminder bell's panel) is a full-height
 * sheet that must sit directly below the sticky top nav. The nav's bottom
 * edge shifts as the (non-sticky) ShopStateNotice scrolls away, so a
 * static offset can't work — track the real bottom while `active` and
 * expose it to the panel via the `--chat-top` CSS variable.
 *
 * Shared by `ChatIndicator` and `ReminderBell`: both mount a panel with the
 * same `.chat-panel` mobile layout, but never both at once for the same
 * viewer (buyers see the former, staff the latter) — each still needs its
 * own call since `ChatIndicator` never mounts for staff.
 */
export function useChatTopOffset(active: boolean, containerRef: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    if (!active) return undefined;
    const nav = containerRef.current?.closest('.top-nav');
    if (!nav) return undefined;
    const root = document.documentElement;
    const update = () => root.style.setProperty('--chat-top', `${Math.round(nav.getBoundingClientRect().bottom)}px`);
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      root.style.removeProperty('--chat-top');
    };
  }, [active, containerRef]);
}

export default useChatTopOffset;
